// ============================================
// XMO.09 D#2 — Analyst Review Dashboard
//
// Admin-only page where Kevin reviews flagged biomarker values,
// confirms/corrects/flags for re-extraction, and submits resolutions.
//
// Spec: HLX-XMO.09 Directive #2
// Route: /admin/analyst-reviews
//
// Design: DM Sans body, no framer-motion, pure CSS transitions.
// Min font 13px. Tailwind named classes. Light theme. Rose accent.
// ============================================

import { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  ClipboardCheck,
  Pencil,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Clock,
  FileText,
} from 'lucide-react';
import {
  fetchReviews,
  fetchReview,
  markInReview,
  submitReview,
  type AnalystReviewDocument,
  type FlaggedItem,
  type ResolvedItemInput,
  type NormalizerUpdateInput,
} from '../../../services/analyst/analystReviewService';

// ── Types ───────────────────────────────────────────────────────────

type ItemAction = 'CONFIRMED' | 'CORRECTED' | 'REMOVED';

interface ItemResolution {
  action: ItemAction;
  resolvedValue: string;
  resolvedCanonicalName: string;
  unit: string;
  correctionReason: string;
}

type SubmitPhase = 'idle' | 'confirming' | 'submitting' | 'success' | 'error';

// ── Helpers ─────────────────────────────────────────────────────────

function formatDate(ts: { toDate(): Date } | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function slaStatus(deadline: { toDate(): Date } | null): 'ok' | 'warning' | 'overdue' {
  if (!deadline) return 'ok';
  try {
    const now = Date.now();
    const dl = deadline.toDate().getTime();
    const hoursRemaining = (dl - now) / (1000 * 60 * 60);
    if (hoursRemaining < 0) return 'overdue';
    if (hoursRemaining < 2) return 'warning';
    return 'ok';
  } catch {
    return 'ok';
  }
}

const SLA_STYLES: Record<ReturnType<typeof slaStatus>, string> = {
  ok: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  overdue: 'bg-red-100 text-red-700',
};

const SLA_LABELS: Record<ReturnType<typeof slaStatus>, string> = {
  ok: 'On time',
  warning: 'Due soon',
  overdue: 'Overdue',
};

const TRIGGER_LABELS: Record<string, string> = {
  'XMO.03': 'Max retakes exceeded',
  'XMO.05': 'Unrecognized biomarker',
  'CAPTAIN_LOW_CONFIDENCE': 'Low confidence extraction',
};

const ITEM_TYPE_LABELS: Record<FlaggedItem['type'], string> = {
  unrecognized_biomarker: 'Unrecognized biomarker',
  low_confidence_extraction: 'Low confidence',
  unreadable_value: 'Unreadable value',
  retake_exceeded: 'Retake exceeded',
};

// ── Queue List Item ─────────────────────────────────────────────────

function QueueListItem({
  review,
  selected,
  onSelect,
}: {
  review: AnalystReviewDocument;
  selected: boolean;
  onSelect: () => void;
}) {
  const sla = slaStatus(review.slaDeadline);
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 border-b border-zinc-100 transition-colors
        ${selected ? 'bg-rose-50 border-l-2 border-l-rose-600' : 'bg-white hover:bg-zinc-50 border-l-2 border-l-transparent'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-zinc-900 truncate">
          {review.flaggedItems.length} flagged item{review.flaggedItems.length !== 1 ? 's' : ''}
        </span>
        <span className={`text-[13px] leading-tight px-2 py-0.5 rounded font-medium ${SLA_STYLES[sla]}`}>
          {SLA_LABELS[sla]}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
        <span>{TRIGGER_LABELS[review.triggerSource] ?? review.triggerSource}</span>
        <span className="text-zinc-300">·</span>
        <span>{formatDate(review.createdAt)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-sm text-zinc-400">
        <span className="truncate font-mono text-[13px] leading-tight">{review.reviewId.slice(0, 12)}…</span>
        <ChevronRight size={14} className="flex-shrink-0" />
      </div>
    </button>
  );
}

// ── Flagged Item Card ───────────────────────────────────────────────

function FlaggedItemCard({
  item,
  resolution,
  onChange,
}: {
  item: FlaggedItem;
  resolution: ItemResolution;
  onChange: (res: ItemResolution) => void;
}) {
  return (
    <div className="rounded border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <span className="text-sm font-semibold text-zinc-900">{item.biomarkerName || item.itemId}</span>
          <span className="ml-2 text-[13px] leading-tight text-zinc-500">{ITEM_TYPE_LABELS[item.type]}</span>
        </div>
        <span className="text-[13px] leading-tight px-2 py-0.5 rounded bg-zinc-100 text-zinc-600 font-mono">
          {Math.round(item.confidenceScore * 100)}% conf
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-3">
        <div>
          <label className="text-[13px] leading-tight font-medium text-zinc-500 block mb-1">Extracted value</label>
          <div className="text-sm font-mono text-zinc-800 bg-zinc-50 rounded px-2 py-1 border border-zinc-200">
            {item.extractedValue || '—'}
          </div>
        </div>
        <div>
          <label className="text-[13px] leading-tight font-medium text-zinc-500 block mb-1">Trigger</label>
          <div className="text-sm text-zinc-800 bg-zinc-50 rounded px-2 py-1 border border-zinc-200">
            {TRIGGER_LABELS[item.triggerSource] ?? item.triggerSource}
          </div>
        </div>
      </div>

      {item.notes && (
        <p className="mt-2 text-sm text-zinc-600 italic">{item.notes}</p>
      )}

      {/* Action selector */}
      <div className="mt-3 flex gap-2">
        <ActionButton
          active={resolution.action === 'CONFIRMED'}
          onClick={() => onChange({ ...resolution, action: 'CONFIRMED', resolvedValue: item.extractedValue })}
          icon={<ClipboardCheck size={14} />}
          label="Confirm"
        />
        <ActionButton
          active={resolution.action === 'CORRECTED'}
          onClick={() => onChange({ ...resolution, action: 'CORRECTED' })}
          icon={<Pencil size={14} />}
          label="Correct"
        />
        <ActionButton
          active={resolution.action === 'REMOVED'}
          onClick={() => onChange({ ...resolution, action: 'REMOVED' })}
          icon={<RotateCcw size={14} />}
          label="Flag re-extract"
        />
      </div>

      {/* Correction form — visible when CORRECTED */}
      {resolution.action === 'CORRECTED' && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="text-[13px] leading-tight font-medium text-zinc-500 block mb-1">Corrected value</label>
            <input
              type="text"
              value={resolution.resolvedValue}
              onChange={(e) => onChange({ ...resolution, resolvedValue: e.target.value })}
              className="w-full text-sm rounded border border-zinc-300 px-2 py-1.5 focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none"
              placeholder="Enter corrected value"
            />
          </div>
          <div>
            <label className="text-[13px] leading-tight font-medium text-zinc-500 block mb-1">Unit</label>
            <input
              type="text"
              value={resolution.unit}
              onChange={(e) => onChange({ ...resolution, unit: e.target.value })}
              className="w-full text-sm rounded border border-zinc-300 px-2 py-1.5 focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none"
              placeholder="e.g., mg/dL"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[13px] leading-tight font-medium text-zinc-500 block mb-1">Correction reason</label>
            <input
              type="text"
              value={resolution.correctionReason}
              onChange={(e) => onChange({ ...resolution, correctionReason: e.target.value })}
              className="w-full text-sm rounded border border-zinc-300 px-2 py-1.5 focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none"
              placeholder="e.g., OCR misread decimal"
            />
          </div>
        </div>
      )}

      {/* Normalizer update form — visible for unrecognized_biomarker */}
      {item.type === 'unrecognized_biomarker' && resolution.action !== 'REMOVED' && (
        <div className="mt-3">
          <label className="text-[13px] leading-tight font-medium text-zinc-500 block mb-1">Canonical name (for normalizer)</label>
          <input
            type="text"
            value={resolution.resolvedCanonicalName}
            onChange={(e) => onChange({ ...resolution, resolvedCanonicalName: e.target.value })}
            className="w-full text-sm rounded border border-zinc-300 px-2 py-1.5 focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none"
            placeholder="e.g., High-Sensitivity CRP"
          />
        </div>
      )}
    </div>
  );
}

// ── Action Button ───────────────────────────────────────────────────

function ActionButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border transition-colors
        ${active
          ? 'bg-rose-600 text-white border-rose-600'
          : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50'}`}
    >
      {icon}
      {label}
    </button>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────

export function AnalystReviewDashboard() {
  const [reviews, setReviews] = useState<AnalystReviewDocument[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<AnalystReviewDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  // Per-item resolutions
  const [itemResolutions, setItemResolutions] = useState<Record<string, ItemResolution>>({});
  const [analystNotes, setAnalystNotes] = useState('');
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Load queue ──
  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statuses = showResolved
        ? ['PENDING', 'ASSIGNED', 'IN_REVIEW', 'ESCALATED', 'RESOLVED'] as const
        : ['PENDING', 'ASSIGNED', 'IN_REVIEW', 'ESCALATED'] as const;
      const data = await fetchReviews([...statuses]);
      setReviews(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  // ── Select a review ──
  const handleSelect = useCallback(async (reviewId: string) => {
    setSelectedId(reviewId);
    setSubmitPhase('idle');
    setSubmitError(null);
    setAnalystNotes('');

    try {
      const review = await fetchReview(reviewId);
      if (!review) {
        setError('Review not found');
        return;
      }
      setSelectedReview(review);

      // Initialize item resolutions
      const resolutions: Record<string, ItemResolution> = {};
      for (const item of review.flaggedItems) {
        resolutions[item.itemId] = {
          action: 'CONFIRMED',
          resolvedValue: item.extractedValue || '',
          resolvedCanonicalName: '',
          unit: '',
          correctionReason: '',
        };
      }
      setItemResolutions(resolutions);

      // Transition to IN_REVIEW if ASSIGNED or REASSIGNED (uses transaction to prevent concurrent claims)
      if (review.status === 'ASSIGNED' || review.status === 'REASSIGNED') {
        try {
          await markInReview(reviewId);
        } catch (lockErr) {
          setError(
            lockErr instanceof Error && lockErr.message.includes('cannot transition')
              ? 'This review is already being reviewed by another analyst.'
              : lockErr instanceof Error ? lockErr.message : 'Failed to claim review',
          );
          setSelectedReview(null);
          setSelectedId(null);
          return;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load review');
    }
  }, []);

  // ── Update an item resolution ──
  const updateItemResolution = useCallback((itemId: string, res: ItemResolution) => {
    setItemResolutions((prev) => ({ ...prev, [itemId]: res }));
  }, []);

  // ── Submit resolution ──
  const handleSubmit = useCallback(async () => {
    if (!selectedReview) return;

    if (submitPhase === 'idle') {
      setSubmitPhase('confirming');
      return;
    }

    if (submitPhase !== 'confirming') return;

    setSubmitPhase('submitting');
    setSubmitError(null);

    try {
      // Build resolved items
      const resolvedItems: ResolvedItemInput[] = selectedReview.flaggedItems.map((item) => {
        const res = itemResolutions[item.itemId];
        return {
          itemId: item.itemId,
          action: res.action,
          originalValue: item.extractedValue || '',
          resolvedValue: res.action === 'CORRECTED' ? res.resolvedValue : item.extractedValue,
          resolvedCanonicalName: res.resolvedCanonicalName || undefined,
          unit: res.unit || undefined,
          correctionReason: res.correctionReason || undefined,
        };
      });

      // Build normalizer updates for unrecognized biomarkers with canonical names
      const normalizerUpdates: NormalizerUpdateInput[] = selectedReview.flaggedItems
        .filter((item) => {
          const res = itemResolutions[item.itemId];
          return item.type === 'unrecognized_biomarker' && res.resolvedCanonicalName;
        })
        .map((item) => {
          const res = itemResolutions[item.itemId];
          return {
            rawName: item.biomarkerName,
            normalizedName: res.resolvedCanonicalName,
            action: 'ADD_ALIAS' as const,
            recommendedBy: 'kevin',
          };
        });

      await submitReview(selectedReview.reviewId, {
        resolvedItems,
        normalizerUpdates,
        notes: analystNotes,
        analystId: 'kevin',
        analystDisplayName: 'Kevin Wallquist',
      });

      setSubmitPhase('success');

      // Refresh queue after short delay
      setTimeout(() => {
        loadReviews();
        setSelectedId(null);
        setSelectedReview(null);
        setSubmitPhase('idle');
      }, 2000);
    } catch (err) {
      setSubmitPhase('error');
      setSubmitError(err instanceof Error ? err.message : 'Submission failed');
    }
  }, [selectedReview, itemResolutions, analystNotes, submitPhase, loadReviews]);

  const cancelSubmit = useCallback(() => {
    setSubmitPhase('idle');
    setSubmitError(null);
  }, []);

  // ── Render ──
  const activeCount = reviews.filter((r) => r.status !== 'RESOLVED').length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Analyst Review Queue
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              XMO.09 — Review flagged biomarker values
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                className="rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
              />
              Show resolved
            </label>
            <button
              onClick={loadReviews}
              className="px-3 py-1.5 text-sm rounded border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              Refresh
            </button>
            <span className="text-sm font-medium text-zinc-600">
              {activeCount} active
            </span>
          </div>
        </div>
      </div>

      <div className="flex" style={{ height: 'calc(100vh - 73px)' }}>
        {/* Queue list */}
        <div className="w-80 flex-shrink-0 border-r border-zinc-200 bg-white overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-zinc-400" />
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center">
              <AlertTriangle size={20} className="mx-auto text-red-400 mb-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <CheckCircle2 size={24} className="mx-auto text-green-400 mb-2" />
              <p className="text-sm text-zinc-500">No reviews pending</p>
            </div>
          ) : (
            reviews.map((review) => (
              <QueueListItem
                key={review.reviewId}
                review={review}
                selected={selectedId === review.reviewId}
                onSelect={() => handleSelect(review.reviewId)}
              />
            ))
          )}
        </div>

        {/* Detail panel */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedReview ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileText size={32} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-sm text-zinc-500">Select a review to begin</p>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl">
              {/* Review header */}
              <div className="bg-white rounded border border-zinc-200 p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-zinc-900">Review Details</h2>
                    <p className="text-sm text-zinc-500 font-mono mt-0.5">{selectedReview.reviewId}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={selectedReview.status} />
                    <SlaBadge deadline={selectedReview.slaDeadline} />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Session</span>
                    <p className="font-mono text-zinc-800 truncate">{selectedReview.sessionId}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Patient</span>
                    <p className="font-mono text-zinc-800 truncate">{selectedReview.patientId}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Created</span>
                    <p className="text-zinc-800">{formatDate(selectedReview.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Resolved state */}
              {selectedReview.status === 'RESOLVED' && (
                <div className="rounded border border-green-200 bg-green-50 p-4 mb-4 flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Review resolved</p>
                    <p className="text-sm text-green-700 mt-0.5">
                      {formatDate(selectedReview.resolvedAt)}
                    </p>
                  </div>
                </div>
              )}

              {/* Flagged items */}
              <h3 className="text-sm font-semibold text-zinc-700 mb-2">
                Flagged Items ({selectedReview.flaggedItems.length})
              </h3>
              <div className="space-y-3 mb-4">
                {selectedReview.flaggedItems.map((item) => (
                  <FlaggedItemCard
                    key={item.itemId}
                    item={item}
                    resolution={itemResolutions[item.itemId] ?? {
                      action: 'CONFIRMED',
                      resolvedValue: item.extractedValue || '',
                      resolvedCanonicalName: '',
                      unit: '',
                      correctionReason: '',
                    }}
                    onChange={(res) => updateItemResolution(item.itemId, res)}
                  />
                ))}
              </div>

              {/* Analyst notes */}
              {selectedReview.status !== 'RESOLVED' && (
                <>
                  <h3 className="text-sm font-semibold text-zinc-700 mb-2">Analyst Notes</h3>
                  <textarea
                    value={analystNotes}
                    onChange={(e) => setAnalystNotes(e.target.value)}
                    className="w-full text-sm rounded border border-zinc-300 px-3 py-2 focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none resize-y min-h-[80px] mb-4"
                    placeholder="Internal notes about this review (not patient-facing)..."
                    rows={3}
                  />

                  {/* Confirm dialog */}
                  {submitPhase === 'confirming' && (
                    <div className="rounded border border-amber-300 bg-amber-50 p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-900">Confirm submission</p>
                          <p className="mt-1 text-sm text-amber-800">
                            This will write corrections to the patient&apos;s record and notify them
                            that their complete results are ready. This action cannot be undone.
                          </p>
                          <ul className="mt-2 text-sm text-amber-800 space-y-1">
                            {selectedReview.flaggedItems.map((item) => {
                              const res = itemResolutions[item.itemId];
                              return (
                                <li key={item.itemId}>
                                  <span className="font-medium">{item.biomarkerName}:</span>{' '}
                                  {res.action === 'CONFIRMED' && 'Confirm extracted value'}
                                  {res.action === 'CORRECTED' && `Correct to ${res.resolvedValue}`}
                                  {res.action === 'REMOVED' && 'Flag for re-extraction'}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2 justify-end">
                        <button
                          onClick={cancelSubmit}
                          className="px-3 py-1.5 text-sm rounded border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSubmit}
                          className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white hover:bg-rose-700 transition-colors font-medium"
                        >
                          Confirm &amp; Submit
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Submit button */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSubmit}
                      disabled={submitPhase === 'submitting' || submitPhase === 'success'}
                      className={`flex items-center gap-2 px-4 py-2 text-sm rounded font-medium transition-colors
                        ${submitPhase === 'success'
                          ? 'bg-green-600 text-white'
                          : submitPhase === 'submitting'
                            ? 'bg-zinc-400 text-white cursor-not-allowed'
                            : 'bg-rose-600 text-white hover:bg-rose-700'}`}
                    >
                      {submitPhase === 'submitting' && <Loader2 size={14} className="animate-spin" />}
                      {submitPhase === 'success' && <CheckCircle2 size={14} />}
                      {submitPhase === 'success'
                        ? 'Submitted'
                        : submitPhase === 'submitting'
                          ? 'Submitting…'
                          : 'Submit Review'}
                    </button>

                    {submitPhase === 'error' && submitError && (
                      <p className="text-sm text-red-600">{submitError}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    PENDING: 'bg-zinc-100 text-zinc-700',
    ASSIGNED: 'bg-blue-100 text-blue-700',
    IN_REVIEW: 'bg-purple-100 text-purple-700',
    RESOLVED: 'bg-green-100 text-green-700',
    ESCALATED: 'bg-red-100 text-red-700',
    REASSIGNED: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`text-[13px] leading-tight px-2 py-0.5 rounded font-medium ${styles[status] ?? 'bg-zinc-100 text-zinc-600'}`}>
      {status}
    </span>
  );
}

// ── SLA Badge ───────────────────────────────────────────────────────

function SlaBadge({ deadline }: { deadline: { toDate(): Date } | null }) {
  const sla = slaStatus(deadline);
  return (
    <span className={`flex items-center gap-1 text-[13px] leading-tight px-2 py-0.5 rounded font-medium ${SLA_STYLES[sla]}`}>
      <Clock size={12} />
      {SLA_LABELS[sla]}
    </span>
  );
}
